import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, TextField, Typography, CircularProgress, Paper } from "@mui/material";
import Modal from "./Modal";
import { createValidator, compose, required, maxLength, isDirty as dirty } from "../../utils/validationUtils";

const EditInstrumentModal = ({ open = false, onClose, initialData, onSubmit, loading = false }) => {
  const [form, setForm] = useState({ name: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initialData?.name ?? "",
    });
    setErrors({});
    setTouched({});
  }, [open, initialData]);

  const validator = useMemo(
    () =>
      createValidator({
        name: compose(required("El nombre es obligatorio."), maxLength(150)),
      }),
    [],
  );

  const validate = (state = form) => validator(state);

  const isDirty = useMemo(() => {
    const init = {
      name: initialData?.name ?? "",
    };
    return dirty(init, form);
  }, [form, initialData]);

  const handleChange = (field) => (ev) => {
    const value = ev.target.value;
    setForm((s) => ({ ...s, [field]: value }));
    if (touched[field]) setErrors(validate({ ...form, [field]: value }));
  };

  const handleBlur = (field) => () => {
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors(validate());
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault();
    const v = validate();
    setErrors(v);
    setTouched({ name: true });
    if (Object.keys(v).length) return;

    setSubmitting(true);
    let ok = false;
    try {
      ok = await onSubmit?.({
        id: initialData?.id,
        name: form.name.trim(),
      });
    } catch (e) {
      ok = false;
    }

    setSubmitting(false);
    if (ok === true) onClose?.();
  };

  const isSaving = submitting || loading;

  return (
    <Modal open={open} handleClose={onClose} width="580px">
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <form onSubmit={handleSubmit} noValidate>
          <Stack gap={3}>
            {/* Encabezado */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5 }}>
                Actualizar instrumento
              </Typography>

              {/* Meta */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {initialData?.id && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(0,0,0,0.7)",
                      backgroundColor: "rgba(0,0,0,0.08)",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    {`ID instrumento: ${initialData.id}`}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Contenido */}
            <Box sx={{ p: 3, pt: 0 }}>
              <Stack spacing={3}>
                <TextField
                  label="Nombre"
                  value={form.name}
                  onChange={handleChange("name")}
                  onBlur={handleBlur("name")}
                  error={!!errors.name}
                  helperText={errors.name || "Nombre del instrumento"}
                  inputProps={{ maxLength: 150 }}
                  autoFocus
                  required
                  fullWidth
                />
              </Stack>

              {/* Acciones */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 3, mt: 2 }}>
                <Button variant="outlined" onClick={onClose} disabled={isSaving} sx={{ borderRadius: 1, px: 3 }}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSaving || !isDirty || Object.keys(validate()).length > 0}
                  sx={{ minWidth: 120, borderRadius: 1, px: 3 }}
                >
                  {isSaving ? (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <span>Actualizando…</span>
                    </Stack>
                  ) : (
                    "Actualizar"
                  )}
                </Button>
              </Box>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Modal>
  );
};

export default EditInstrumentModal;
